import Fuse from "fuse.js";

// Common stop words to ignore in search
const STOP_WORDS = new Set([
  "a", "an", "the", "and", "or", "but", "in", "on", "at", "to", "for",
  "of", "with", "by", "from", "is", "it", "as", "be", "was", "are",
  "been", "being", "have", "has", "had", "do", "does", "did", "will",
  "would", "could", "should", "may", "might", "shall", "can", "this",
  "that", "these", "those", "i", "me", "my", "we", "our", "you", "your",
  "he", "she", "they", "them", "their", "its", "not", "no", "so",
  "if", "then", "than", "too", "very", "just", "about", "up", "out",
  "all", "some", "any", "each", "every", "both", "few", "more", "most",
  "other", "into", "over", "after", "before", "between", "under",
  "again", "further", "once", "here", "there", "when", "where", "how",
  "what", "which", "who", "whom", "why",
]);

// Strip common suffixes for basic stemming
function stem(word: string): string {
  if (word.length < 4) return word;
  // Handle -ing
  if (word.endsWith("ing")) {
    const base = word.slice(0, -3);
    if (base.length >= 3) return base;
    // double consonant: running -> run
    if (base.length >= 2 && base[base.length - 1] === base[base.length - 2]) {
      return base.slice(0, -1);
    }
    return base + "e"; // hiking -> hike
  }
  // Handle -s / -es plurals
  if (word.endsWith("ies") && word.length > 4) return word.slice(0, -3) + "y";
  if (word.endsWith("ses") || word.endsWith("xes") || word.endsWith("zes")) return word.slice(0, -2);
  if (word.endsWith("ches") || word.endsWith("shes")) return word.slice(0, -2);
  if (word.endsWith("s") && !word.endsWith("ss") && !word.endsWith("us")) return word.slice(0, -1);
  // Handle -ed
  if (word.endsWith("ed") && word.length > 4) {
    const base = word.slice(0, -2);
    if (base.length >= 3) return base;
    return base + "e";
  }
  return word;
}

// Normalize a query: remove stop words, stem remaining words
export function normalizeQuery(query: string): string {
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0 && !STOP_WORDS.has(w))
    .map(stem)
    .join(" ");
}

export interface FuzzySearchOptions<T> {
  keys: (keyof T | string)[];
  threshold?: number;
  includeScore?: boolean;
}

export function createFuzzySearch<T>(items: T[], options: FuzzySearchOptions<T>) {
  const fuse = new Fuse(items, {
    keys: options.keys as string[],
    threshold: options.threshold ?? 0.4,
    includeScore: options.includeScore ?? true,
    ignoreLocation: true,
    findAllMatches: true,
    minMatchCharLength: 2,
    getFn: (obj: any, path: string | string[]) => {
      const value = Fuse.config.getFn(obj, path);
      if (typeof value === "string") {
        return normalizeQuery(value);
      }
      if (Array.isArray(value)) {
        return value.map((v) => (typeof v === "string" ? normalizeQuery(v) : v));
      }
      return value;
    },
  });

  return (query: string) => {
    if (!query.trim()) return items;
    const normalized = normalizeQuery(query);
    if (!normalized) return items;
    return fuse.search(normalized).map((r) => r.item);
  };
}

/* ------------------------------------------------------------------ */
/*  Ranked keyword search with synonym expansion                       */
/*  Used by the inventory grid: token-AND semantics, OR'd synonyms,    */
/*  per-field weights, relevance-sorted output.                        */
/* ------------------------------------------------------------------ */

/** Token-level shopper vocabulary: stemmed token → extra acceptable phrases. */
const TOKEN_SYNONYMS: Record<string, string[]> = {
  motorhome: ["motor home", "class a", "class b", "class c"],
  camper: ["travel trailer", "pop up", "folding"],
  popup: ["pop up", "folding"],
  "5th": ["fifth wheel"],
  fifth: ["fifth wheel"],
  toyhauler: ["toy hauler"],
  bunkhouse: ["bunk"],
  bunk: ["bunkhouse"],
  coach: ["motor home", "class a", "class b", "class c"],
};

/** Multi-word phrases → treated as one requirement with alternatives. */
const PHRASE_SYNONYMS: [string, string[]][] = [
  // Coach classes must stay whole phrases, otherwise "class a" also matches
  // Class B/C (the bare "a" is a stop word and gets dropped).
  ["class a", ["motor home class a", "motorhome class a"]],
  ["class b", ["motor home class b", "motorhome class b"]],
  ["class c", ["motor home class c", "motorhome class c"]],
  ["motor home", ["motorhome", "class a", "class b", "class c"]],
  ["5th wheel", ["fifth wheel"]],
  ["fifth wheel", ["5th wheel"]],
  ["pop up", ["folding pop up", "folding"]],
  ["toy hauler", ["toyhauler"]],
  ["travel trailer", ["camper"]],
  ["destination trailer", ["park model"]],
];

/**
 * Split shopper shorthand written without spaces so the phrase matcher can
 * see it ("classa" → "class a", "fifthwheel" → "fifth wheel").
 */
const GLUED_FORMS: [RegExp, string][] = [
  [/\bclass\s*([abc])\b/g, "class $1"],
  [/\b(fifth|5th)\s*wheels?\b/g, "$1 wheel"],
  [/\bfifthwheels?\b/g, "fifth wheel"],
  [/\b5thwheels?\b/g, "5th wheel"],
  [/\btoyhaulers?\b/g, "toy hauler"],
  [/\btravel\s*trailers?\b/g, "travel trailer"],
  [/\bmotor\s*homes?\b/g, "motor home"],
  [/\bmotorhomes?\b/g, "motor home"],
  [/\bpop\s*ups?\b/g, "pop up"],
];

function expandGlued(text: string): string {
  let out = text.toLowerCase();
  for (const [re, rep] of GLUED_FORMS) out = out.replace(re, rep);
  return out;
}

/**
 * Normalize text for matching: lowercase, punctuation → spaces (so "pop-up"
 * and "pop up" are equivalent), stem tokens. Stop words are KEPT here —
 * unlike normalizeQuery — because they matter inside phrases ("class a").
 */
function tokenizeForMatch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map(stem)
    .join(" ");
}

/**
 * Build AND-groups of OR-alternatives from a raw query.
 * Each inner array is one requirement; any alternative satisfies it.
 */
export function buildSearchGroups(query: string): string[][] {
  const norm = tokenizeForMatch(expandGlued(query));
  if (!norm) return [];
  const words = norm.split(" ");
  const groups: string[][] = [];
  const consumed = new Set<number>();

  // Phrase-level synonyms first (they consume their tokens).
  for (const [phrase, alts] of PHRASE_SYNONYMS) {
    const pw = tokenizeForMatch(phrase).split(" ");
    for (let i = 0; i + pw.length <= words.length; i++) {
      if (pw.every((w, j) => words[i + j] === w)) {
        groups.push([tokenizeForMatch(phrase), ...alts.map(tokenizeForMatch)]);
        pw.forEach((_, j) => consumed.add(i + j));
      }
    }
  }

  words.forEach((w, i) => {
    if (consumed.has(i) || STOP_WORDS.has(w)) return;
    const alts = new Set<string>([w]);
    for (const syn of TOKEN_SYNONYMS[w] ?? []) alts.add(tokenizeForMatch(syn));
    groups.push([...alts]);
  });

  return groups;
}

export interface RankedField {
  value: string | number | string[] | null | undefined;
  weight: number;
}

/**
 * Token-AND ranked search. Every query token (or one of its synonyms) must
 * match at least one field; results are sorted by summed field weights.
 */
export function rankSearch<T>(
  items: T[],
  query: string,
  getFields: (item: T) => RankedField[],
): T[] {
  const groups = buildSearchGroups(query);
  if (groups.length === 0) return items;

  const prepared = items.map((item) => ({
    item,
    fields: getFields(item)
      .map((f) => ({
        weight: f.weight,
        hay: tokenizeForMatch(
          Array.isArray(f.value) ? f.value.join(" ") : String(f.value ?? ""),
        ),
      }))
      .filter((f) => f.hay.length > 0)
      .map((f) => ({ ...f, words: f.hay.split(" ") })),
  }));

  const scored: { item: T; score: number }[] = [];
  for (const { item, fields } of prepared) {
    let score = 0;
    let matchedAll = true;
    for (const group of groups) {
      let best = 0;
      for (const alt of group) {
        if (!alt) continue;
        const altIsPhrase = alt.includes(" ");
        for (const f of fields) {
          let s = 0;
          if (f.hay === alt) s = f.weight * 1.2;
          else if (altIsPhrase && f.hay.includes(alt)) s = f.weight;
          else if (!altIsPhrase && f.words.includes(alt)) s = f.weight;
          // Substring fallback only for alts long enough to be meaningful —
          // otherwise "class c" would match every word containing "c".
          else if (alt.length >= 3 && f.hay.includes(alt)) s = f.weight * 0.6;
          if (s > best) best = s;
        }
      }
      if (best === 0) {
        matchedAll = false;
        break;
      }
      score += best;
    }
    if (matchedAll) scored.push({ item, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((s) => s.item);
}
